/**
 * InlineChipCard — anchored metadata + actions for the focused AI chip.
 *
 * Atomic chips: text edits happen inline in the chip's inner contenteditable
 * span (the ChipView NodeView). This card is metadata-only — it shows the
 * candidate attribute pills (cortex pipeline) and exposes Remove / Next /
 * Jump buttons. No text input means no `draft` divergence (#362).
 *
 * The card subscribes to PM via a manual `editor.on("update")` + selectionUpdate
 * listener inside an effect (NOT `useEditorState`). Tiptap's
 * ReactNodeViewRenderer mounts each chip's NodeView in its own React root,
 * which means useEditorState bound to a Jotai-providered store would see a
 * different store than the chip view; the manual subscription keeps state
 * consistent across roots.
 */

import React from "react";
import { useAtomValue } from "jotai";
import { NodeSelection } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import {
  reportFocusAtom,
  getFocusedChipId,
  inlineCardOpenChipIdAtom,
} from "../../atoms/reporterChipAtoms";
import { findFieldNodeById } from "../tiptap/extensions";
import { emit } from "../../lib/viewerBus";
import type { CandidateAttribute, Phrasing } from "../../hooks/useReportQuery";
import {
  defaultAttributeSelection,
  hasEditableAttributePills,
  joinPhrasing,
  joinPhrasingWithMeasurement,
  parseChipPhraseSources,
  phrasingDefaultSelection,
  rebuildPhraseFromCondition,
  toggleAttrSelection,
  togglePhrasingSelection,
} from "../../utils/reportUtils";
import { logger } from "../../utils/logger";

const DIM_LABELS: Record<string, string> = {
  laterality: "Laterality",
  size: "Size",
  severity: "Severity",
  acuity: "Acuity",
  obstruction: "Obstruction",
  calcification_status: "Calcification",
};
const DIM_ORDER = [
  "laterality",
  "size",
  "severity",
  "acuity",
  "obstruction",
  "calcification_status",
];

interface ChipInfo {
  id: string;
  text: string;
  kind: string | null;
  seriesId: string | null;
  sliceIndex: number | null;
  frameOfReferenceUid: string | null;
  candidates: CandidateAttribute[];
  /** Engine-emitted structured phrase. When present, pills + the live re-render
   *  come from it (join its pre-rendered tokens, no client-side clinical logic);
   *  null for pre-rollout studies, which fall back to the candidates path. */
  phrasing: Phrasing | null;
  /** AI condition slug (e.g. "liver/steatosis"). Stable across pill toggles
   *  so the rebuilt phrase doesn't drift on every click. */
  condition: string | null;
  /** User-entered measurement, when one has been added. A pill toggle on a
   *  measured chip re-appends the suffix (and suppresses the size descriptor)
   *  rather than dropping it; null when no measurement is present. */
  measurement: { axesCm: [number, number, number]; volumeMl: number } | null;
  pos: number;
  domRect: { top: number; left: number; width: number; bottom: number };
  parentRect: { top: number; left: number };
}

export interface InlineChipCardProps {
  editor: Editor | null;
  containerEl: HTMLElement | null;
}

function findFocusedChipNode(
  editor: Editor,
  focusedChipId: string,
): { node: ProseMirrorNode; pos: number } | null {
  let found: { node: ProseMirrorNode; pos: number } | null = null;
  editor.state.doc.descendants((n, pos) => {
    if (found) return false;
    if (n.type.name !== "field") return true;
    if ((n.attrs.id as string | null) === focusedChipId) {
      found = { node: n, pos };
      return false;
    }
    return true;
  });
  return found;
}

export function InlineChipCard({ editor, containerEl }: InlineChipCardProps) {
  // Project the SSoT down to a chipId — both chip-focused (Tab landed)
  // and caret-in-chip (mouse clicked into inner text) mount the card.
  const reportFocus = useAtomValue(reportFocusAtom);
  const focusedChipId = getFocusedChipId(reportFocus);
  // The card only renders when opened via "Edit attributes" (the chip
  // right-click menu or the E key) — openEditAttrsForChip sets openChipId.
  // Plain click and Tab nav focus the chip without opening the card, so it
  // never occludes the findings below.
  const openChipId = useAtomValue(inlineCardOpenChipIdAtom);

  // Subscribe to the focused chip's attrs via editor events — every doc
  // change re-runs the lookup. When attrs change (text rewrite, pill
  // toggle, AI revision), the card re-renders without imperative refresh.
  const [chipNodeData, setChipNodeData] = React.useState<{
    attrs: Record<string, unknown>;
    pos: number;
  } | null>(null);
  React.useEffect(() => {
    if (!editor || !focusedChipId) {
      setChipNodeData(null);
      return;
    }
    const update = () => {
      const found = findFocusedChipNode(editor, focusedChipId);
      if (!found) {
        setChipNodeData(null);
        return;
      }
      setChipNodeData({ attrs: { ...found.node.attrs }, pos: found.pos });
    };
    update();
    editor.on("update", update);
    editor.on("selectionUpdate", update);
    return () => {
      editor.off("update", update);
      editor.off("selectionUpdate", update);
    };
  }, [editor, focusedChipId]);

  // Local override of attribute pill state. null = use default selection.
  const [editedSelected, setEditedSelected] = React.useState<Record<string, string | null> | null>(
    null,
  );

  // Reset pill state when switching chips.
  React.useLayoutEffect(() => {
    setEditedSelected(null);
  }, [focusedChipId]);

  // Callback ref measures cardHeight on mount + content changes via
  // ResizeObserver. The card mounts several renders after openChipId
  // flips — info materializes via the chipNodeData → computeInfo chain —
  // so a layout effect keyed on openChipId would find the element still
  // null and never re-fire. cardHeight feeds the placement math below;
  // visibility:hidden until measured avoids a one-frame overlap at
  // top:-gap when cardHeight is still 0.
  const [cardHeight, setCardHeight] = React.useState<number | null>(null);
  const roRef = React.useRef<ResizeObserver | null>(null);
  const measureRef = React.useCallback((el: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) {
      setCardHeight(null);
      return;
    }
    setCardHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => setCardHeight(el.offsetHeight));
    ro.observe(el);
    roRef.current = ro;
  }, []);

  // Build chip info (including DOM rect) — derived from PM state. Recomputed
  // on chip change and on scroll/resize.
  const [info, setInfo] = React.useState<ChipInfo | null>(null);
  const computeInfo = React.useCallback(() => {
    if (!editor || !focusedChipId || !containerEl || !chipNodeData) {
      setInfo(null);
      return;
    }
    const dom = editor.view.nodeDOM(chipNodeData.pos) as HTMLElement | null;
    if (!dom) {
      setInfo(null);
      return;
    }
    const rect = dom.getBoundingClientRect();
    const cRect = containerEl.getBoundingClientRect();
    // Same shape-checked parse the entry-point gate (chipEditability) uses.
    // The gate that decided to offer "Edit attributes" and this render gate
    // must read the chip through identical parsing, or corrupt JSON makes
    // them diverge (gate offers, card renders nothing — the #703 class).
    const sources = parseChipPhraseSources(chipNodeData.attrs);
    const candidates = sources?.candidates ?? [];
    const phrasing = sources?.phrasing ?? null;
    const measurementAttr = chipNodeData.attrs.measurement as string | null;
    let measurement: ChipInfo["measurement"] = null;
    if (measurementAttr) {
      try {
        measurement = JSON.parse(measurementAttr);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("InlineChipCard: failed to parse data-measurement", err, measurementAttr);
      }
    }
    setInfo({
      id: focusedChipId,
      text: (chipNodeData.attrs.text as string | null) ?? "",
      kind: (chipNodeData.attrs.kind as string | null) ?? null,
      seriesId: (chipNodeData.attrs.seriesId as string | null) ?? null,
      sliceIndex:
        typeof chipNodeData.attrs.sliceIndex === "number"
          ? (chipNodeData.attrs.sliceIndex as number)
          : null,
      frameOfReferenceUid: (chipNodeData.attrs.frameOfReferenceUid as string | null) ?? null,
      candidates,
      phrasing,
      condition: (chipNodeData.attrs.condition as string | null) ?? null,
      measurement,
      pos: chipNodeData.pos,
      domRect: { top: rect.top, left: rect.left, width: rect.width, bottom: rect.bottom },
      parentRect: { top: cRect.top, left: cRect.left },
    });
  }, [editor, focusedChipId, containerEl, chipNodeData]);

  React.useLayoutEffect(() => {
    computeInfo();
  }, [computeInfo]);

  React.useEffect(() => {
    if (!info || !containerEl) return;
    const handler = () => computeInfo();
    containerEl.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      containerEl.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [info, containerEl, computeInfo]);

  if (!editor || !info) return null;
  if (info.kind !== "ai-finding") return null;
  // The card opens via openEditAttrsForChip (the chip right-click menu's
  // "Edit attributes" item, or the E shortcut), which sets openChipId.
  // Gating on id-match rather than clearing the atom on every focus change
  // lets the rad leave the chip and come back without the card reopening —
  // it stays closed until the next explicit "Edit attributes".
  if (info.id !== openChipId) return null;

  // Two pill sources. Engine-emitted `phrasing` is preferred: its options are
  // pre-rendered tokens, so the live re-render just joins them — no client-side
  // clinical logic, no drift from what the engine emitted. Falls back to the
  // candidates path (rebuildPhrase from the condition slug) for pre-rollout
  // studies that carry no phrasing. SuggestionStrip mirrors this so both AI
  // surfaces behave alike.
  const usePhrasing = info.phrasing != null && info.phrasing.dims.length > 0;
  const hasPills = hasEditableAttributePills(info.phrasing, info.candidates, info.condition);
  // The card is attribute-pills only now. Nothing to show without a pill source —
  // don't pop an empty box. Entry points (menu item, E shortcut) gate on the
  // same hasEditableAttributePills predicate, so reaching here without pills
  // means a stale entry path — they toast instead of silently no-oping.
  if (!hasPills) return null;
  const currentSelected =
    editedSelected ??
    (usePhrasing
      ? phrasingDefaultSelection(info.phrasing!)
      : defaultAttributeSelection(info.candidates));

  // Normalized pill rows {dim, label, options:[{value, label, pct}]} from whichever
  // source is active, so the JSX below is source-agnostic.
  type PillRow = {
    dim: string;
    label: string;
    options: { value: string; label: string; pct: number }[];
  };
  let pillRows: PillRow[];
  if (usePhrasing) {
    pillRows = info.phrasing!.dims.map((d) => ({
      dim: d.dim,
      label: DIM_LABELS[d.dim] ?? d.dim,
      options: d.options.map((o) => ({
        value: o.value,
        label: o.text,
        pct: Math.round(o.probability * 100),
      })),
    }));
  } else {
    const grouped: Record<string, CandidateAttribute[]> = {};
    for (const c of info.candidates) {
      (grouped[c.dim] ??= []).push(c);
    }
    for (const k of Object.keys(grouped)) {
      grouped[k].sort((a, b) => b.probability - a.probability);
    }
    const presentDims = Object.keys(grouped);
    const orderedDims = [
      ...DIM_ORDER.filter((d) => presentDims.includes(d)),
      ...presentDims.filter((d) => !DIM_ORDER.includes(d)).sort(),
    ];
    pillRows = orderedDims.map((dim) => ({
      dim,
      label: DIM_LABELS[dim] ?? dim,
      options: grouped[dim].map((c) => ({
        value: c.value,
        label: c.value.replace(/_/g, " "),
        pct: Math.round(c.probability * 100),
      })),
    }));
  }

  const togglePill = (dim: string, value: string) => {
    let next: Record<string, string | null>;
    let newPhrase: string;
    if (usePhrasing) {
      next = togglePhrasingSelection(info.phrasing!, currentSelected, dim, value);
      // A measured chip keeps its measurement across toggles: re-append the
      // suffix (which also suppresses the size descriptor) rather than dropping
      // it — `phrasing` itself has no measurement slot.
      newPhrase = info.measurement
        ? joinPhrasingWithMeasurement(info.phrasing!, next, info.measurement)
        : joinPhrasing(info.phrasing!, next);
    } else {
      if (info.condition === null) {
        // Unreachable behind the hasPills gate (the candidates path requires
        // a condition) — defensive-only, but a defensive bail still logs.
        logger.warn("chip_attr_toggle_no_condition id=%s", info.id);
        return;
      }
      next = toggleAttrSelection(currentSelected, dim, value);
      newPhrase = rebuildPhraseFromCondition(info.condition, next, info.candidates);
    }
    setEditedSelected(next);
    // Resolve the chip by id at dispatch time — info.pos is a snapshot from
    // the last computeInfo render, and any transaction landing in between
    // (impression stream, chip GC, autosave-adjacent updates) shifts it.
    // Same hardening as replaceChipText / handleVoiceAdjustTranscript; this
    // was the one text-mutating call site still writing through a cached pos.
    const applied = editor
      .chain()
      .replaceChipText(info.id, newPhrase)
      .command(({ tr, dispatch }) => {
        const hit = findFieldNodeById(tr.doc, info.id);
        if (!hit) return false;
        // Keep PM selection on the chip so navigation-focus keyboard
        // shortcuts (E/M/⌫) stay armed across consecutive pill clicks —
        // the behavior the old setSelection(info.pos) provided.
        if (dispatch) tr.setSelection(NodeSelection.create(tr.doc, hit.pos));
        return true;
      })
      .run();
    if (!applied) {
      // Same surface-don't-swallow treatment as the entry points: a pill
      // click that visibly does nothing reads as a broken control.
      logger.warn("chip_attr_toggle_stale_chip id=%s", info.id);
      emit("toast", { message: "Couldn't update this finding — it's no longer in the report." });
    }
  };

  // Prefer above the chip: the rad has already considered earlier findings
  // (above), so occluding them is acceptable; the upcoming findings (below)
  // must stay visible. Flip below only when there isn't room above —
  // chip near the top of the report or a short scroll viewport.
  const chipTopInContainer = info.domRect.top - info.parentRect.top;
  const chipBottomInContainer = info.domRect.bottom - info.parentRect.top;
  const gap = 6;
  const sideGap = 8;
  const placeAbove = cardHeight === null || chipTopInContainer >= cardHeight + gap;
  const top = placeAbove
    ? chipTopInContainer - (cardHeight ?? 0) - gap
    : chipBottomInContainer + gap;

  return (
    <div
      ref={measureRef}
      className="rj-inline-card"
      style={{
        top,
        // Anchor to both edges of the report column so the card spans the
        // full width — without this the card shifts horizontally with each
        // chip's left position, making it look like it's moving L↔R as the
        // card opens on different findings. CSS `width: 320px` and
        // `max-width: min(360px, 100%)` are explicitly overridden here.
        left: sideGap,
        right: sideGap,
        width: "auto",
        maxWidth: "none",
        visibility: cardHeight === null ? "hidden" : "visible",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="rj-inline-card__attrs">
        {pillRows.map((row) => (
          <div key={row.dim} className="rj-inline-card__attr-row">
            <span className="rj-inline-card__attr-label">{row.label}</span>
            <div className="rj-inline-card__attr-pills">
              {row.options.map((o) => {
                const selected = currentSelected[row.dim] === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={
                      "rj-inline-card__pill " + (selected ? "rj-inline-card__pill--on" : "")
                    }
                    onClick={() => togglePill(row.dim, o.value)}
                  >
                    <span className="rj-inline-card__pill-value">{o.label}</span>
                    <span className="rj-inline-card__pill-prob">{o.pct}%</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
